import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import MenuLayout from "./MenuLayout";
import CryptoNetworkLogo from "./CryptoNetworkLogo";
import {
  cancelCryptoPayment,
  getPaymentStatus,
  submitCryptoTransactionHash,
} from "../api/cart";
import "../styles/QrDepositPage.css";

const TERMINAL_STATUSES = new Set(["confirmed", "rejected", "expired", "canceled"]);
const ACTIVE_PAYMENT_STATUSES = new Set([
  "monitoring",
  "awaiting_hash",
  "detected",
  "verifying",
  "fulfillment_pending",
]);

const formatRemaining = (seconds) => {
  const safe = Math.max(0, seconds);
  const minutes = Math.floor(safe / 60);
  const rest = safe % 60;
  return `${String(minutes).padStart(2, "0")}:${String(rest).padStart(2, "0")}`;
};

const redirectPath = (itemType) => {
  if (itemType === "wallet") return "/wallet";
  if (itemType === "competition") return "/competitions/mine";
  return "/challenges";
};

const QrDepositPage = () => {
  const { state } = useLocation();
  const { paymentId } = useParams();
  const navigate = useNavigate();
  const [payment, setPayment] = useState(state?.payment || null);
  const [loading, setLoading] = useState(!state?.payment);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [remaining, setRemaining] = useState(0);
  const [cancelStep, setCancelStep] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [transactionHash, setTransactionHash] = useState("");
  const [submittingHash, setSubmittingHash] = useState(false);
  const [redirectSeconds, setRedirectSeconds] = useState(5);
  const reference = paymentId || payment?.payment_id;

  const loadPayment = async (silent = false) => {
    if (!reference) return;
    if (!silent) setLoading(true);
    try {
      const response = await getPaymentStatus(reference);
      setPayment(response);
      setError("");
    } catch (err) {
      if (!silent) setError(err?.message || "اطلاعات پرداخت دریافت نشد.");
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!payment || payment.payment_id !== reference) loadPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference]);

  useEffect(() => {
    if (!reference || !payment || TERMINAL_STATUSES.has(payment.payment_status)) {
      return undefined;
    }
    const interval = window.setInterval(() => loadPayment(true), 5000);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reference, payment?.payment_status]);

  useEffect(() => {
    const deadline = payment?.valid_until;
    if (!deadline || !["monitoring", "awaiting_hash"].includes(payment?.payment_status)) {
      setRemaining(0);
      return undefined;
    }
    const update = () => {
      const seconds = Math.max(0, Math.ceil((new Date(deadline).getTime() - Date.now()) / 1000));
      setRemaining(seconds);
      if (seconds === 0) loadPayment(true);
    };
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payment?.valid_until, payment?.payment_status]);

  useEffect(() => {
    if (payment?.payment_status !== "confirmed") return undefined;
    setRedirectSeconds(5);
    const target = redirectPath(payment.item_type);
    const interval = window.setInterval(() => {
      setRedirectSeconds((current) => Math.max(0, current - 1));
    }, 1000);
    const timer = window.setTimeout(() => navigate(target, { replace: true }), 5000);
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timer);
    };
  }, [navigate, payment?.item_type, payment?.payment_status]);

  const handleCopy = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setNotice(`${label} کپی شد.`);
    } catch {
      setNotice("کپی خودکار انجام نشد؛ مقدار را دستی کپی کنید.");
    }
  };

  const handleCancel = async () => {
    if (!reference || canceling) return;
    setCanceling(true);
    setError("");
    try {
      const response = await cancelCryptoPayment(reference);
      setPayment(response?.payment || payment);
      setNotice(response?.message || "پرداخت لغو شد.");
      setCancelStep(false);
    } catch (err) {
      setError(err?.message || "لغو پرداخت انجام نشد.");
      setCancelStep(false);
      await loadPayment(true);
    } finally {
      setCanceling(false);
    }
  };

  const handleHashSubmit = async (event) => {
    event.preventDefault();
    const normalized = transactionHash.trim();
    if (!/^(0x)?[0-9a-fA-F]{64}$/.test(normalized)) {
      setError("هش تراکنش معتبر نیست؛ هش باید شامل ۶۴ کاراکتر هگزادسیمال باشد.");
      return;
    }
    if (!reference || submittingHash) return;
    setSubmittingHash(true);
    setError("");
    setNotice("");
    try {
      const response = await submitCryptoTransactionHash(reference, normalized);
      setPayment(response?.payment || payment);
      setNotice(
        response?.message ||
          "هش ثبت شد و پرداخت شما در صف بررسی شبکه قرار گرفت. نتیجه با ایمیل اطلاع داده می‌شود."
      );
    } catch (err) {
      setError(err?.message || "ثبت هش تراکنش انجام نشد.");
    } finally {
      setSubmittingHash(false);
    }
  };

  const statusClass = useMemo(() => {
    if (payment?.payment_status === "confirmed") return "success";
    if (["rejected", "expired", "canceled"].includes(payment?.payment_status)) return "danger";
    if (["detected", "verifying", "fulfillment_pending"].includes(payment?.payment_status)) return "pending";
    return "waiting";
  }, [payment?.payment_status]);

  const stage = useMemo(() => {
    const status = payment?.payment_status;
    if (status === "confirmed") return 3;
    if (status === "fulfillment_pending") return 2;
    if (["detected", "verifying"].includes(status)) return 1;
    return 0;
  }, [payment?.payment_status]);

  if (loading) {
    return <MenuLayout><div className="qr-page"><div className="qr-state-card">در حال دریافت اطلاعات پرداخت...</div></div></MenuLayout>;
  }

  if (!payment || error === "Payment not found.") {
    return (
      <MenuLayout><div className="qr-page"><div className="qr-state-card danger">
        <h2>درخواست پرداخت پیدا نشد</h2>
        <button type="button" onClick={() => navigate("/cart")}>بازگشت به سبد خرید</button>
      </div></div></MenuLayout>
    );
  }

  const isActive = ACTIVE_PAYMENT_STATUSES.has(payment.payment_status);
  const canCancel = ["monitoring", "awaiting_hash"].includes(payment.payment_status) && remaining > 0;

  return (
    <MenuLayout>
      <div className="qr-page">
        <header className="qr-header">
          <div>
            <span className="qr-eyebrow">پرداخت مستقیم USDT</span>
            <h1 className="qr-title">ارسال وجه و ثبت هش تراکنش</h1>
            <p className="qr-subtitle">پس از واریز، هش تراکنش را ثبت کنید تا پرداخت روی شبکه بررسی شود.</p>
          </div>
          <div className={`qr-status-pill ${statusClass}`}>{payment.status_message || "در انتظار پرداخت"}</div>
        </header>

        {(notice || error) && <div className={`qr-alert ${error ? "danger" : "success"}`} role={error ? "alert" : "status"} aria-live="polite">{error || notice}</div>}

        {isActive && (
          <div className="qr-progress" aria-label="مراحل تأیید پرداخت">
            {["در انتظار واریز", "بررسی هش", "تأیید شبکه", "تکمیل سفارش"].map((label, index) => (
              <div className={`qr-progress-step ${index < stage ? "done" : ""} ${index === stage ? "active" : ""}`} key={label}>
                <span>{index < stage ? "✓" : index + 1}</span><strong>{label}</strong>
              </div>
            ))}
          </div>
        )}

        {payment.payment_status === "confirmed" ? (
          <section className="qr-success-scene">
            <div className="qr-success-ring" aria-hidden="true"><span>✓</span></div>
            <span className="qr-success-kicker">پرداخت موفق</span>
            <h2>مبلغ شما دریافت و تأیید شد</h2>
            <p>خرید یا شارژ حساب با موفقیت انجام شد. نتیجه پرداخت نیز برای شما ایمیل شد.</p>
            <div className="qr-redirect"><span>انتقال خودکار تا</span><strong>{redirectSeconds}</strong><span>ثانیه دیگر</span></div>
            <button type="button" onClick={() => navigate(redirectPath(payment.item_type), { replace: true })}>ادامه</button>
          </section>
        ) : ["rejected", "expired", "canceled"].includes(payment.payment_status) ? (
          <section className={`qr-final-scene ${payment.payment_status}`}>
            <div className="qr-final-icon">{payment.payment_status === "canceled" ? "×" : "!"}</div>
            <h2>{payment.payment_status === "canceled" ? "پرداخت لغو شد" : payment.payment_status === "expired" ? "زمان پرداخت پایان یافت" : "پرداخت تأیید نشد"}</h2>
            <p>{payment.status_message}</p>
            <div className="qr-final-actions">
              <button type="button" onClick={() => navigate("/cart")}>بازگشت به سبد خرید</button>
              {payment.payment_status !== "canceled" && <button type="button" className="secondary" onClick={() => navigate("/tickets")}>ارسال تیکت</button>}
            </div>
          </section>
        ) : (
          <div className="qr-payment-shell">
            <section className="qr-visual-card">
              <div className="qr-network-row">
                <CryptoNetworkLogo network={payment.network} className="qr-network-mark" />
                <div><strong>{payment.network_label}</strong><span>USDT</span></div>
              </div>
              <div className="qr-code-box">
                {payment.qr_image_url ? <img src={payment.qr_image_url} width="192" height="192" alt="QR آدرس پرداخت" /> : <div className="qr-code-placeholder">QR</div>}
              </div>
              <p className="qr-scan-help">QR را با کیف پول خود اسکن کنید. این آدرس در مدت پرداخت برای ترکیب همین مبلغ رزرو شده است.</p>
            </section>

            <section className="qr-info-card">
              {["monitoring", "awaiting_hash"].includes(payment.payment_status) && (
                <div className="qr-countdown"><span>زمان باقی‌مانده برای واریز</span><strong dir="ltr">{formatRemaining(remaining)}</strong></div>
              )}

              <div className="qr-field"><label>مبلغ دقیق قابل پرداخت</label><div className="qr-value-row">
                <strong dir="ltr">{payment.pay_amount} USDT</strong>
                <button type="button" onClick={() => handleCopy(payment.pay_amount, "مبلغ")}>کپی مبلغ</button>
              </div></div>

              <div className="qr-field"><label>آدرس دریافت</label><div className="qr-value-row address">
                <code dir="ltr">{payment.pay_address}</code>
                <button type="button" onClick={() => handleCopy(payment.pay_address, "آدرس")}>کپی آدرس</button>
              </div></div>

              <div className="qr-warning" role="alert">
                <div className="qr-warning-title"><span className="qr-warning-symbol" aria-hidden="true">!</span><strong>هشدار امنیتی مهم</strong></div>
                <ul>
                  <li>فقط روی شبکه <strong>{payment.network_label}</strong> واریز کنید.</li>
                  <li>مبلغ را دقیقاً <strong dir="ltr">{payment.pay_amount} USDT</strong> و در یک تراکنش ارسال کنید.</li>
                  <li>پیش از ارسال، شبکه و چند کاراکتر اول و آخر آدرس را دوباره بررسی کنید.</li>
                  <li>پس از ارسال وجه، هش همان تراکنش را در کادر زیر ثبت کنید و دکمه انصراف را نزنید.</li>
                  <li>هش قدیمی، تکراری یا مربوط به آدرس، مبلغ یا شبکه دیگر تأیید نخواهد شد.</li>
                </ul>
                <p>ارسال با شبکه، آدرس یا مبلغ اشتباه ممکن است باعث از دست رفتن دائمی دارایی شود و قابل بازیابی نباشد.</p>
              </div>

              {["monitoring", "awaiting_hash"].includes(payment.payment_status) && (
                <form className="qr-hash-form" onSubmit={handleHashSubmit}>
                  <label htmlFor="crypto-transaction-hash">هش تراکنش</label>
                  <input
                    id="crypto-transaction-hash"
                    dir="ltr"
                    type="text"
                    value={transactionHash}
                    onChange={(event) => setTransactionHash(event.target.value)}
                    placeholder={payment.network === "usdt_bep20" ? "0x..." : "Transaction ID"}
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck="false"
                    disabled={submittingHash || remaining <= 0}
                    aria-describedby="crypto-hash-help"
                  />
                  <button type="submit" disabled={submittingHash || remaining <= 0}>
                    {submittingHash ? "در حال ثبت..." : "ثبت هش و بررسی پرداخت"}
                  </button>
                  <p id="crypto-hash-help" className="qr-hash-help">
                    هش را از جزئیات برداشت در کیف پول یا صرافی خود کپی کنید. پس از ثبت، بررسی حتی با بستن صفحه ادامه پیدا می‌کند.
                  </p>
                </form>
              )}

              {["detected", "verifying"].includes(payment.payment_status) && (
                <div className="qr-queue-state received"><span className="qr-loader" /><div><strong>هش تراکنش در حال بررسی است</strong><p>می‌توانید صفحه را ببندید؛ بررسی سمت سرور ادامه دارد و نتیجه با ایمیل اعلام می‌شود{payment.chain_confirmations ? ` (${payment.chain_confirmations} تأیید شبکه)` : ""}.</p></div></div>
              )}

              {payment.payment_status === "fulfillment_pending" && (
                <div className="qr-queue-state"><span className="qr-loader" /><div><strong>شبکه پرداخت را تأیید کرد</strong><p>در حال تکمیل نهایی خرید یا شارژ حساب شما هستیم.</p></div></div>
              )}

              {canCancel && (
                <div className="qr-cancel-area">
                  {!cancelStep ? <button type="button" className="qr-cancel-button" onClick={() => setCancelStep(true)}>انصراف از پرداخت</button> : (
                    <div className="qr-cancel-confirm"><strong>مطمئن هستید؟</strong><p>فقط اگر هنوز وجه را ارسال نکرده‌اید انصراف دهید.</p><div>
                      <button type="button" onClick={handleCancel} disabled={canceling}>{canceling ? "در حال بررسی..." : "بله، لغو پرداخت"}</button>
                      <button type="button" className="secondary" onClick={() => setCancelStep(false)} disabled={canceling}>ادامه پرداخت</button>
                    </div></div>
                  )}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </MenuLayout>
  );
};

export default QrDepositPage;
